import { RegistrationPreviousRequest, RegistrationProcessRequest } from "@shared/dtos/requests/registrationRequests.dto"
import { RegistrationResponse } from "@shared/dtos/responses/registrationResponses.dto"
import { Duration } from "@shared/types/duration"

import { axiosPublic } from "./generic/AxiosInterceptor/AxiosInterceptor"
import { makeFileInstance } from "./generic/FileInstance"

/**
 * IT-5095: `/process` runs third-party calls inline and does not fit the global
 * 10s `REQUEST_TIMEOUT` (see `configs/EnvironmentConfig.ts`), which that
 * constant's own comment anticipates ("in the future we may decide to set the
 * timeout per service").
 *
 * The binding case is the ID check: `createScanRequest` retries the iDenfy
 * `/token` POST 3 times at 10s each, so it can take ~31s (IT-4875). Aborting at
 * 10s did NOT cancel that work. The backend still minted a real iDenfy session,
 * but the patient never received the `scanReference`, so the next attempt
 * minted another one and they never got an iframe.
 *
 * Vecozo COV on the Personal step has a similar ~30s ceiling.
 */
const PROCESS_TIMEOUT = Duration.from("45 seconds").milliseconds

/**
 *  Process a part of a registration process. The endpoint will be called multiple times during
 *  a registration process.
 */
export const process = async (request: RegistrationProcessRequest): Promise<RegistrationResponse> => {
  const resp = await axiosPublic({
    url: "/public/registration/process",
    method: "post",
    data: request,
    timeout: PROCESS_TIMEOUT,
  })
  return resp.data
}

/**
 * Navigate to the previous step in the registration process.
 * Pops the last step from history and returns it with its formData.
 */
export const previous = async (request: RegistrationPreviousRequest): Promise<RegistrationResponse> => {
  const resp = await axiosPublic({
    url: "/public/registration/previous",
    method: "post",
    data: request,
  })
  return resp.data
}

/**
 * Fetch a preview of the previous GP letter PDF with placeholder data.
 * Requires encrypted registration data for authorization.
 *
 * @param previousGpRequestApproval - Whether the patient approves medical file transfer (reflected in preview)
 */
export const previewPreviousGpLetter = async (pageId: string, data: string, previousGpRequestApproval: boolean) => {
  const result = await axiosPublic({
    url: `/public/registration/preview/previousgpletter/${pageId}`,
    method: "get",
    params: { data, previousGpRequestApproval },
    responseType: "blob",
  })
  return makeFileInstance(result)
}
